import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  AfterViewChecked,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { TransactionRequest } from 'src/app/model/TransactionRequest';
import { TransactionRequestService } from 'src/app/services/transaction-request/transaction-request.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';

@Component({
  selector: 'app-supplier-transaction-requests-table',
  templateUrl: './supplier-transaction-requests-table.component.html',
  styleUrl: './supplier-transaction-requests-table.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SupplierTransactionRequestsTableComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  /** Dependency injections */
  private _transactionRequestService = inject(TransactionRequestService);
  private _dialogService = inject(DialogService);

  /** Transaction requests to render */
  dataSource = new MatTableDataSource<TransactionRequest>([]);

  /** Loading state */
  loading = true;

  /** Table columns - commented out fields specified by @product */
  displayedColumns: string[] = [
    //'tx_request_id',
    'created_at',
    'buyer_company',
    'amount',
    'status',
    'actions',
  ];

  /** Subscription for data */
  private transactionRequestsSub?: Subscription;

  /** Sort reference */
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  private sortBound = false;

  ngAfterViewChecked(): void {
    if (!this.sortBound && this.sort) {
      this.sortBound = true;

      // bind sort and set default
      this.dataSource.sort = this.sort;
      this.sort.active = 'created_at';
      this.sort.direction = 'desc';
      this.sort.sortChange.emit({ active: 'created_at', direction: 'desc' });

      console.log('[Sort] MatSort bound and default sort applied');
    }
  }

  /**
   * OnInit, subscribe to data changes and initialize data source
   */
  ngAfterViewInit(): void {
    // Just keep the binding here
    this.dataSource.sort = this.sort;
  }

  ngOnInit(): void {
    this.dataSource.sortingDataAccessor = (item, property) => {
      let value;
      switch (property) {
        case 'created_at':
          value =
            item.created_at instanceof Date
              ? item.created_at.getTime()
              : Date.parse(String(item.created_at)) || 0;
          break;
        case 'amount':
          value = Number(item.amount) || 0;
          break;
        case 'buyer_company':
          value = (item.buyer_company || '').toLowerCase();
          break;
        case 'status':
          value = (item.statusProductFormat || '').toLowerCase();
          break;
        default:
          value = (item as any)[property];
      }
      console.log(`[Sort Accessor] property="${property}", extracted=`, value);
      return value;
    };

    this.transactionRequestsSub =
      this._transactionRequestService.changes$.subscribe(
        (transactionRequests: TransactionRequest[]) => {
          this.dataSource.data = transactionRequests || [];
          this.loading = false;

          // ⚡ ensure sort is re-applied after data loads
          if (this.sort) {
            this.dataSource.sort = this.sort;
            this.sort.active = 'created_at';
            this.sort.direction = 'desc';
            this.sort.sortChange.emit({
              active: 'created_at',
              direction: 'desc',
            });
          }
        },
      );
  }

  /**
   * On component destroy, unsubscribe from all subscriptions
   */
  ngOnDestroy(): void {
    if (this.transactionRequestsSub) {
      this.transactionRequestsSub.unsubscribe();
    }
  }

  /**
   * Open the review offer dialog
   *
   * @param transactionRequest The transaction request to review
   */
  openReviewOfferDialog(transactionRequest: TransactionRequest): void {
    // TODO: request/offer flow
    this._dialogService.openReviewOfferDialog(transactionRequest);
    //this._dialogService.openReviewRequestDialog(transactionRequest);
  }

  /**
   * Open the cancel request dialog
   *
   * @param transactionRequest The transaction request to cancel
   */
  openCancelRequestDialog(transactionRequest: TransactionRequest): void {
    this._dialogService.openCancelRequestDialog(transactionRequest.id);
  }

  /**
   * Format currency values
   *
   * @param amount The amount to format
   * @return The formatted currency string
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  /**
   * Format date values
   *
   * @param date The date to format
   * @return The formatted date string
   */
  formatDate(date: Date | null): string {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }
}
